import { Box, Input, InputGroup, InputLeftElement, Text, Badge, Image, useDisclosure, Popover, PopoverTrigger, PopoverContent, PopoverBody, Select, Flex, PopoverCloseButton, Tooltip, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody, Textarea, ModalFooter, Button, } from '@chakra-ui/react'
import MyDiv from './leaveList.style'
import FilterIcon from '../../../../assets/images/Filter.svg'
import { Search2Icon } from '@chakra-ui/icons'
import SortIcon from '../../../../assets/images/SortIcon.svg'
import React, { useState } from 'react';
import AlertCard from '../../../../components/AlertCard'
import { CustomToast, FilterPopover } from '../../../../components'
import { useFetch } from '../../../../hooks/useFetch'
import { ILeaveRequest } from '../../LeaveRequest/modal'
import CustomTables, { Column, ILazyParams } from '../../../../components/CustomTable/CustomTables'
import { IFilterOptions } from '../../../../components/FilterCard/filterCard'
import { showDate } from '../../../../utilities/helper'

type LeaveStatus = "Pending" | "Approved" | "Rejected" | "Cancelled";

const sortOptions = [
  'Leave Applied date (Newest First)',
  'Leave Started Date (Upcoming First)',
  'Name (A–Z)',
  'Name (Z–A)',
  'Leave Duration (Longest to Shortest)',
  'Leave Duration (Shortest to Longest)',
];

const sortMapping: Record<string, { field: string; order: number }> = {
  'Leave Applied date (Newest First)': { field: 'appliedDate', order: -1 },
  'Leave Started Date (Upcoming First)': { field: 'fromDate', order: 1 },
  'Name (A–Z)': { field: 'name', order: 1 },
  'Name (Z–A)': { field: 'name', order: -1 },
  'Leave Duration (Longest to Shortest)': { field: 'duration', order: -1 },
  'Leave Duration (Shortest to Longest)': { field: 'duration', order: 1 },
};

type leaveListProps = {
  leaveList: { data: ILeaveRequest[]; totalCount: number }; onView: any;
  onReload: () => void;
  query: any;
  setSearch: any;
  loading: boolean;
  setQuery: React.Dispatch<React.SetStateAction<any>>;
  filter: IFilterOptions;
  setSelectedItems: React.Dispatch<React.SetStateAction<Record<string, number[]>>>;
}

// short leave times come back as "HH:mm:ss" (TimeOnly) - trim seconds for display
const formatTime = (t?: string | null) => {
  if (!t) return '';
  return t.length >= 5 ? t.slice(0, 5) : t;
};

const LeaveList: React.FC<any> = ({ onView, leaveList, onReload, query, setQuery, setSearch, loading, filter }: leaveListProps) => {
  const sortPop = useDisclosure();
  const filterPop = useDisclosure();
  const { addToast } = CustomToast();
  const { fetchApi, } = useFetch(addToast);
  const [selected, setSelected] = useState<string>(sortOptions[0]);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertStatus, setAlertStatus] = useState<"Approved" | "Rejected" | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [loader, setLoader] = useState(false);

  const tableLazyParams: ILazyParams = {
    first: query.lazyParams.first ?? 0,
    rows: query.lazyParams.rows,
    sortField: query.lazyParams.sortField || undefined,
    sortOrder: query.lazyParams.sortOrder || undefined,
    multiSortMeta: [],
    filters: query.lazyParams.filters ?? {},
    globalFilter: '',
  };

  const handleLazyChange = (p: any) => {
    const filtersChanged =
      JSON.stringify(p.filters ?? {}) !== JSON.stringify(query.lazyParams.filters ?? {});
    const first = filtersChanged ? 0 : p.first;

    setQuery((prev: any) => ({
      ...prev,
      lazyParams: {
        ...prev.lazyParams,
        first,
        rows: p.rows,
        page: Math.floor(first / p.rows),
        sortField: p.sortField ?? '',
        sortOrder: p.sortOrder ?? 1,
        filters: p.filters ?? {},
      },
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Approved':
        return 'green';
      case 'Pending':
        return 'yellow';
      case 'Rejected':
        return 'red';
      case 'Cancelled':
        return 'blue';
      default:
        return 'grey';
    }
  };

  const handleStatusChange = (value: LeaveStatus, id: number) => {
    if (value === "Approved") {
      setAlertStatus("Approved");
      setSelectedId(id);
      setAlertOpen(true);
    }

    if (value === "Rejected") {
      setSelectedId(id);
      setAlertStatus("Rejected");
      setRejectModal(true);
    }
  };

  const handleRejectSubmit = () => {
    if (!rejectReason.trim()) {
      addToast({ message: "Please enter reason!", status: "warning" });
      return;
    }
    setRejectModal(false);
    setAlertOpen(true);
  };

  const handleConfirmStatusChange = async () => {
    if (alertStatus && selectedId != null) {
      setLoader(true);
      let payload: any = {
        id: selectedId,
        status: alertStatus === "Approved" ? true : false,
      };

      if (alertStatus === "Rejected") {
        payload.rejectionReason = rejectReason;
      }

      const response = await fetchApi("Hrlense_Leave/leaveApproval", "PUT", payload, "", "");

      if (response) {
        addToast({ message: "Leave status updated successfully!", status: "success" });
        onReload();
      }
      setAlertOpen(false);
      setRejectReason("");
      setLoader(false);
    }
  };

  const handleSelect = (option: string) => {
    setSelected(option);

    const sortConfig = sortMapping[option];
    if (sortConfig) {
      // query changes -> the parent reloads by itself (the extra onReload() call is not needed any more)
      setQuery((prev: any) => ({
        ...prev,
        lazyParams: {
          ...prev.lazyParams,
          sortField: sortConfig.field,
          sortOrder: sortConfig.order,
          page: 0,
          first: 0
        }
      }));
    }
    sortPop.onClose();
  };

  function stripHtml(html: string) {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  }

  return (
    <MyDiv>
      <Box className="page_wrapper" >
        <Flex className="page_heading flex_header">
          <Text className="font-poppins text_medium text_lg">Leave List</Text>
          <Flex className="filter_section" gap={2}>
            <InputGroup width="250px" height="25px">
              <InputLeftElement pointerEvents="none">
                <Search2Icon color="rgba(102, 112, 133, 1)" />
              </InputLeftElement>
              <Input type="text" className="font-poppins text_regular text_md" placeholder="Search name or emp. code...." onChange={(e) => setSearch(e.target.value)} />
            </InputGroup>

            {/* Filter */}
            <Popover isOpen={filterPop.isOpen} onOpen={filterPop.onOpen} onClose={filterPop.onClose} placement="bottom-end" closeOnBlur>
              <PopoverTrigger>
                <Image src={FilterIcon} alt="Filter" className='icon_btn' cursor="pointer" />
              </PopoverTrigger>
              <FilterPopover
                filter={filter}
                onApplyFilter={(filters) => {
                  const _filter: Record<string, number[]> = {};
                  Object.keys(filters).forEach((key) => {
                    if (filters[key].length > 0) {
                      _filter[key.replace(" ", "")] = filters[key];
                    }
                  });
                  setQuery((prev: any) => ({
                    ...prev,
                    filter: {
                      ..._filter
                    },
                    lazyParams: {
                      ...prev.lazyParams,
                      page: 0,
                      first: 0
                    }
                  }));
                  filterPop.onClose();
                }}
              />
            </Popover>

            {/* Sort */}
            <Popover isOpen={sortPop.isOpen} onOpen={sortPop.onOpen} onClose={sortPop.onClose} placement="bottom-end" closeOnBlur>
              <PopoverTrigger>
                <Image src={SortIcon} alt="Sort" className="icon_btn" cursor="pointer" />
              </PopoverTrigger>
              <PopoverContent w="270px" borderRadius="10px" boxShadow="md" textAlign={'center'}>
                <Box px={4} py={2} borderBottom="1px solid #eee">
                  <Text className='font-poppins text_sm text_medium' textAlign="center" color={"#00000080"}>Sort By</Text>
                  <PopoverCloseButton top="8px" right="12px" />
                </Box>
                <PopoverBody p={0}>
                  {sortOptions.map((option) => (
                    <Box key={option} px={4} py={2} cursor="pointer" bg={selected === option ? '#EBEBEB' : 'white'} _hover={{ bg: '#EBEBEB' }} onClick={() => handleSelect(option)}>
                      <Text className='font-poppins font_dark' fontSize="14px" fontWeight={selected === option ? 600 : 500}>{option}</Text>
                    </Box>
                  ))}
                </PopoverBody>
              </PopoverContent>
            </Popover>
          </Flex>
        </Flex>

        {/* table is ALWAYS rendered (no spinner swap) - `loading` goes to the table itself, same as Branch */}
        <Box position="relative" minH="300px">
          <CustomTables<any>
            value={leaveList.data}
            loading={loading}
            emptyMessage="No Leave Data Available"
            preferenceKey="leaveList"
            showGlobalSearch={false}
            showToolbar={true}
            lazy
            lazyParams={tableLazyParams}
            onLazyParamsChange={handleLazyChange}
            totalRecords={leaveList.totalCount}
            showPagination
            rowsPerPageOptions={[20, 50, 100, 200]}
            maxTableHeight={null}
          >
            <Column header="S. No" body={(_, index) => query.lazyParams.first + index + 1} />

            <Column header="Emp. Code" field="empId" filterable filterType="text"
              body={(row: any) => (
                <Badge variant="subtle" className="empId font-poppins text_medium text_sm" cursor="pointer" onClick={() => onView(row)} >
                  {row.empId}
                </Badge>
              )}
            />

            <Column header="Name" field="name" sortable filterable filterType="text" />
            <Column header="Company" field="companyName" filterable filterType="text" body={(row: any) => row.companyName || "--"} />
            <Column header="Branch" field="branchName" filterable filterType="text" body={(row: any) => row.branchName || "--"} />
            <Column header="Type" body={(row: any) => row.isShortLeave ? (
                  <Badge colorScheme="purple" className="font-poppins text_xsm text_medium">
                    Short Leave
                  </Badge>
                ) : (
                  <Text className="font-poppins text_xsm text_regular" color="gray.500">Full/Half Day</Text>
                )
              }
            />

            <Column header="From Date" field="fromDate" sortable body={(row) => showDate(row.fromDate)} />

            <Column header="Session" field="startSession" filterable filterType="text"
              body={(row: any) =>
                row.isShortLeave
                  ? <Text className="font-poppins text_sm">{formatTime(row.startTime)}</Text>
                  : <Text className="font-poppins text_sm">{row.startSession || '-'}</Text>
              }
            />

            <Column
              header="To Date"
              body={(row: any) => row.isShortLeave ? showDate(row.fromDate) : showDate(row.toDate)}
            />

            <Column header="Session" field="endSession" filterable filterType="text"
              body={(row: any) =>
                row.isShortLeave
                  ? <Text className="font-poppins text_sm">{formatTime(row.endTime)}</Text>
                  : <Text className="font-poppins text_sm">{row.endSession || '-'}</Text>
              }
            />

            <Column header="Duration" field="duration" sortable
              body={(row: any) =>
                row.isShortLeave
                  ? <Text className="font-poppins text_sm">{formatTime(row.startTime)} - {formatTime(row.endTime)}</Text>
                  : <Text className="font-poppins text_sm">{row.totalDays}</Text>
              }
            />

            <Column header="Applied Date" field="appliedDate" sortable body={(row) => showDate(row.appliedDate)} />

            <Column header="Reason" body={(row: any) => (
                <Tooltip
                  label={
                    <Box whiteSpace="normal" dangerouslySetInnerHTML={{ __html: row.reason }} />}
                  aria-label="Full reason"
                  bg="gray.700"
                  color="white"
                  placement="bottom-start"
                  maxW="300px"
                  hasArrow
                >
                  <Box as="span" display="inline-block" maxW='7vw' isTruncated>{stripHtml(row.reason)}</Box>
                </Tooltip>
              )}
            />

            <Column header="Approval Manager" field="approvalManager" filterable filterType="text" />

            <Column header="Action" body={(row: any) => (
                row.status === "Pending" ? (
                  row.canApprove ? (
                    <Select
                      className='font-poppins text_regular text_sm'
                      placeholder="Pending"
                      size="sm"
                      onChange={(e) => handleStatusChange(e.target.value as LeaveStatus, row.id)}
                    >
                      <option value="Approved">Approve</option>
                      <option value="Rejected">Reject</option>
                    </Select>
                  ) : (
                    <Text className='font-poppins text_medium text_xsm' color="gray.500">Pending</Text>
                  )
                ) : (
                  row.status === "Rejected" && row.rejectReason ? (
                    <Tooltip label={<Box whiteSpace="normal" dangerouslySetInnerHTML={{ __html: row.rejectReason }} />} aria-label="Reject reason" bg="gray.700" color="white" placement="bottom-start" maxW="300px" hasArrow>
                      <Text className='font-poppins text_medium text_xsm' color={getStatusColor(row.status)} cursor="help" >
                        {`Rejected by ${row.actionBy} ${`(${showDate(row.action_At)})`}`}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text className='font-poppins text_medium text_xsm' color={getStatusColor(row.status)}>
                      {row.status === "Approved" && `Approved by ${row.actionBy} ${`(${showDate(row.action_At)})`}`}
                      {row.status === "Rejected" && `Rejected by ${row.actionBy} ${`(${showDate(row.action_At)})`}`}
                      {row.status === "Cancelled" && "Cancelled By Employee"}
                    </Text>
                  )
                )
              )}
            />

            <Column
              header="Leave Application"
              body={(row: any) => (
                <Text className="view font-poppins text_medium text_sm" cursor="pointer" onClick={() => onView(row)} >
                  View
                </Text>
              )}
            />
          </CustomTables>
        </Box>
      </Box>

      <Modal isOpen={rejectModal} onClose={() => setRejectModal(false)} isCentered size="lg">
        <ModalOverlay />
        <ModalContent borderRadius="12px">
          <ModalHeader className='font-poppins text_medium'>Reject Reason</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box height="150px">
              <Textarea
                placeholder="Enter reject reason..."
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                className='font-poppins text_regular'
                height="120px"
              />
            </Box>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" onClick={() => setRejectModal(false)}>
              Cancel
            </Button>

            <Button bg="#006838" color="white" ml={3} onClick={handleRejectSubmit}>
              Submit
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <AlertCard
        isOpen={alertOpen}
        onClose={() => setAlertOpen(false)}
        onConfirm={handleConfirmStatusChange}
        title="Approve Leave"
        description={`Are you sure you want to ${alertStatus?.toLowerCase()} leave request?`}
        confirmLabel="Confirm"
        isLoading={loader}
        colorScheme={alertStatus === "Approved" ? "green" : "red"}
      />
    </MyDiv >
  )
};

export default LeaveList